/**
 * The shared preset validator — the ONLY sanctioned way to turn untrusted JSON
 * into a Preset. Imported by the web builder, the Node conformance harness,
 * and (Phase 4) the Workers backend, so validation is the same code everywhere
 * by construction.
 *
 * Two stages:
 *   1. Structural — Ajv against schema/kalmtide.schema.json.
 *   2. Cross-field — the invariants JSON Schema cannot express (marked
 *      "Validator:" in the schema file): segment ordering/contiguity/coverage,
 *      times bounded by durationSec, unique layer ids, derivable sidebands.
 *
 * Errors are human-readable strings with JSON-path-style locations. This
 * format is deliberate: the backend's /generate endpoint feeds these messages
 * back to the LLM for its single repair pass, so they must state what is wrong
 * AND what would be right.
 *
 * Workers note (Phase 4): Ajv compiles validators with `new Function`, which
 * Cloudflare Workers forbid. When wiring workers/, precompile the structural
 * validator with `ajv` standalone codegen at build time; the cross-field stage
 * below is plain code and runs anywhere as-is.
 */

import Ajv2020 from "ajv/dist/2020.js";
import addFormats from "ajv-formats";
import schemaJson from "./kalmtide.schema.json" with { type: "json" };
import type { Layer, Preset } from "./types.ts";
import { crossFieldErrors } from "./crossfield.ts";

export type ValidationResult =
  | { ok: true; preset: Preset }
  | { ok: false; errors: string[] };

const ajv = new Ajv2020({ allErrors: true, strict: true });
addFormats(ajv);
const structural = ajv.compile(schemaJson);

/**
 * Legacy alias: the product launched development as WaveKraft, and presets
 * minted before the rename carry `"schema": "wavekraft/1"` (cloud rows,
 * share links). Accept them forever, normalized to kalmtide/1 — the input
 * document itself is never mutated.
 */
function normalizeLegacy(doc: unknown): unknown {
  if (
    typeof doc === "object" && doc !== null &&
    (doc as { schema?: unknown }).schema === "wavekraft/1"
  ) {
    return { ...(doc as Record<string, unknown>), schema: "kalmtide/1" };
  }
  return doc;
}

/** Validate untrusted JSON against the kalmtide/1 contract. */
export function validatePreset(input: unknown): ValidationResult {
  const doc = normalizeLegacy(input);
  if (!structural(doc)) {
    const errors = (structural.errors ?? []).map(
      (e) => `${e.instancePath || "/"}: ${e.message ?? "invalid"}`,
    );
    return { ok: false, errors: dedupe(errors) };
  }
  // Structurally valid ⇒ safe to treat as Preset for cross-field checks.
  const preset = doc as unknown as Preset;
  const errors = crossFieldErrors(preset);
  return errors.length > 0 ? { ok: false, errors } : { ok: true, preset };
}

function dedupe(items: string[]): string[] {
  return [...new Set(items)];
}

export type { Preset, Layer };
